import { svgIconPaths } from '@/assets/svg/icons';
import Dropdown from '@/components/atoms/Dropdown';
import SvgIcon from '@/components/atoms/SvgIcon';
import colors from '@/theme/colors';
import {
  CONTENT_TYPES,
  CONTENT_VISIBILITY_FILTERS,
  TContentType,
  TContentVisibilityFilter,
} from '@/types/Content.types';
import { CloseIcon } from '@chakra-ui/icons';
import {
  Button,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Spinner,
} from '@chakra-ui/react';
import { debounce } from 'lodash';
import React, { useEffect, useMemo, useRef, useState } from 'react';

const SEARCH_DEBOUNCE_MS = 400;
const TYPE_ALL = 'All';
const TYPE_OPTIONS = [TYPE_ALL, ...CONTENT_TYPES] as const;
const VISIBILITY_OPTIONS = CONTENT_VISIBILITY_FILTERS;

interface Props {
  searchText: string;
  type?: TContentType;
  // Visibility filter is optional — branded (MyOmnia) content is always public,
  // so that screen hides the control.
  showVisibility?: boolean;
  visibility?: TContentVisibilityFilter;
  isSearching: boolean;
  onSearchChange: (value: string) => void;
  onTypeChange: (value?: TContentType) => void;
  onVisibilityChange?: (value: TContentVisibilityFilter) => void;
}

const ContentModerationFilters = ({
  searchText,
  type,
  showVisibility = true,
  visibility = 'all',
  isSearching,
  onSearchChange,
  onTypeChange,
  onVisibilityChange,
}: Props) => {
  // Local copy so the input stays responsive while the URL-bound value updates on a debounce.
  const [localSearchText, setLocalSearchText] = useState(searchText);

  // Keep in sync when the value changes externally (browser back/forward, clear, deep link).
  useEffect(() => {
    setLocalSearchText(searchText);
  }, [searchText]);

  // Stable debounced emitter that always calls the latest handler.
  const onSearchChangeRef = useRef(onSearchChange);
  onSearchChangeRef.current = onSearchChange;
  const debouncedEmit = useMemo(
    () => debounce((value: string) => onSearchChangeRef.current(value), SEARCH_DEBOUNCE_MS),
    [],
  );
  useEffect(() => () => debouncedEmit.cancel(), [debouncedEmit]);

  const handleSearchChange = (value: string) => {
    setLocalSearchText(value);
    debouncedEmit(value);
  };

  const clearSearch = () => {
    debouncedEmit.cancel();
    setLocalSearchText('');
    onSearchChange('');
  };

  return (
    <HStack gap={'15px'}>
      <Dropdown
        title="Type"
        defaultOption={TYPE_ALL}
        selectedOption={type ?? TYPE_ALL}
        options={TYPE_OPTIONS}
        formatValues
        onOptionClick={(option) =>
          onTypeChange(option === TYPE_ALL ? undefined : (option as TContentType))
        }
      />
      {showVisibility && (
        <Dropdown
          title="Visibility"
          defaultOption={'all'}
          selectedOption={visibility}
          options={VISIBILITY_OPTIONS}
          formatValues
          onOptionClick={(option) => onVisibilityChange?.(option as TContentVisibilityFilter)}
        />
      )}
      <InputGroup width={'280px'}>
        <InputLeftElement pointerEvents={'none'} height={'100%'}>
          <SvgIcon iconPath={svgIconPaths.magnifyingGlass} color={colors.text.mediumGray} />
        </InputLeftElement>
        <Input
          backgroundColor={'white'}
          placeholder={'Search by title or author'}
          _placeholder={{ textOverflow: 'ellipsis', overflow: 'hidden' }}
          value={localSearchText}
          onChange={(event) => handleSearchChange(event.target.value)}
        />
        {(isSearching || localSearchText.length > 0) && (
          <InputRightElement width={'48px'} height={'100%'}>
            {isSearching ? (
              <Spinner size={'sm'} color={colors.text.mediumGray} />
            ) : (
              <Button height={'30px'} width={'30px'} minWidth={'30px'} onClick={clearSearch}>
                <CloseIcon color={colors.text.mediumGray} boxSize={3} />
              </Button>
            )}
          </InputRightElement>
        )}
      </InputGroup>
    </HStack>
  );
};

export default ContentModerationFilters;
